import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import BarChartIcon from '@mui/icons-material/BarChart';
import dayjs from 'dayjs';
import { useApi } from '../../../hooks/useApi';
import { RoomStatsDialog } from '../components/RoomStatsDialog';

const SUB_STATUS_LABELS = {
  active: 'Active',
  trialing: 'Trialing',
  'trial-ended': 'Trial ended',
  canceled: 'Canceled',
  cancelled: 'Cancelled',
  past_due: 'Past due',
  none: 'No subscription',
};

const formatSubStatus = (status) =>
  SUB_STATUS_LABELS[status] || (status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown');

function RoomDetails() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const [filterClosed, setFilterClosed] = useState(false);
  const [hideSystemUsers, setHideSystemUsers] = useState(true);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['room', roomId],
    queryFn: () => api.get(`/api/rooms/${roomId}`),
  });

  // Live Stripe-confirmed subscription status for the room owner. Fetched
  // separately so the details view renders immediately and isn't blocked on
  // the Stripe round-trip.
  const { data: ownerSub, isLoading: ownerSubLoading } = useQuery({
    queryKey: ['room-owner-subscription', roomId],
    queryFn: () => api.get(`/api/rooms/${roomId}/owner-subscription`),
    enabled: !!roomId,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data?.room) {
    return (
      <Box>
        <Typography variant="h5">Room not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/rooms')}>
          Back to Rooms
        </Button>
      </Box>
    );
  }

  const { room, participants = [], recordings = [], streamings = [], transcriptions = [] } = data;

  // Calculate duration
  const duration = room.timeEnded
    ? dayjs(room.timeEnded).diff(dayjs(room.timeStarted), 'minute')
    : dayjs().diff(dayjs(room.timeStarted), 'minute');

  // Separate human participants from system users
  const humanParticipants = participants.filter(p => !p.isSystemUser);
  const systemUsers = participants.filter(p => p.isSystemUser);

  // Filter displayed participants based on toggles
  const filteredParticipants = participants.filter(p => {
    if (filterClosed && p.timeLeft) return false;
    if (hideSystemUsers && p.isSystemUser) return false;
    return true;
  });

  // Count is always human participants only (excluding system users)
  const participantCount = filterClosed
    ? humanParticipants.filter(p => !p.timeLeft).length
    : humanParticipants.length;

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/rooms')}
        sx={{ mb: 2 }}
      >
        Back to Rooms
      </Button>
      <Typography variant="h4" gutterBottom>
        Room Details: {room.name}
      </Typography>
      {/* Basic Room Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Basic Room Information</Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<BarChartIcon />}
            disabled={!room.active}
            onClick={() => setStatsDialogOpen(true)}
          >
            Stats
          </Button>
        </Box>
        <Grid container spacing={4}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>Room ID</Typography>
            <Typography variant="body1" sx={{ mb: 2, fontFamily: 'monospace' }}>{room.id}</Typography>

            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>Name</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>{room.name}</Typography>

            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>Owner</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="body1">{room.owner || 'N/A'}</Typography>
              {ownerSubLoading ? (
                <CircularProgress size={14} />
              ) : ownerSub?.subscribed ? (
                <Tooltip title={`${ownerSub.planId ? `Plan: ${ownerSub.planId}. ` : ''}${ownerSub.confirmed ? 'Confirmed live via Stripe' : 'From profile — not confirmed live'}`}>
                  <Chip
                    label={ownerSub.status === 'trialing' ? 'Trialing' : 'Subscribed'}
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                </Tooltip>
              ) : ownerSub?.found && ownerSub.status && ownerSub.status !== 'none' ? (
                <Tooltip title={ownerSub.confirmed ? `Subscription status: ${ownerSub.status}` : `Stored status: ${ownerSub.status} (unconfirmed${ownerSub.error ? ` — ${ownerSub.error}` : ''})`}>
                  <Chip label={formatSubStatus(ownerSub.status)} color="default" size="small" variant="outlined" />
                </Tooltip>
              ) : ownerSub ? (
                <Tooltip title="No subscription on file for this owner">
                  <Chip label="No subscription" size="small" variant="outlined" />
                </Tooltip>
              ) : null}
            </Box>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>Time Started</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {dayjs(room.timeStarted).format('YYYY-MM-DD HH:mm:ss')}
            </Typography>

            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>Time Ended</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {room.timeEnded ? dayjs(room.timeEnded).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
            </Typography>

            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>Duration</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>{duration} mins</Typography>

            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>Status</Typography>
            <Chip
              label={room.active ? 'Active' : 'Ended'}
              color={room.active ? 'success' : 'default'}
              size="small"
              sx={{ mt: 0.5 }}
            />

            {room.roomIP && (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mt: 2
                  }}>MediaServer IP</Typography>
                <Typography variant="body1">{room.roomIP}</Typography>
              </>
            )}
          </Grid>
        </Grid>
      </Paper>
      {/* Participants */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Participants ({participantCount})
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={hideSystemUsers}
                  onChange={(e) => setHideSystemUsers(e.target.checked)}
                  size="small"
                />
              }
              label={`Hide system users${systemUsers.length > 0 ? ` (${systemUsers.length})` : ''}`}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={filterClosed}
                  onChange={(e) => setFilterClosed(e.target.checked)}
                  size="small"
                />
              }
              label="Hide disconnected"
            />
          </Box>
        </Box>
        {filteredParticipants.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User ID</TableCell>
                  <TableCell>Time Joined</TableCell>
                  <TableCell>Time Left</TableCell>
                  <TableCell>Time In Room</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredParticipants.map((participant) => {
                  const timeInRoom = participant.timeLeft
                    ? dayjs(participant.timeLeft).diff(dayjs(participant.timeJoined), 'minute')
                    : dayjs().diff(dayjs(participant.timeJoined), 'minute');
                  return (
                    <TableRow key={participant.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {participant.odotId || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>{dayjs(participant.timeJoined).format('HH:mm:ss')}</TableCell>
                      <TableCell>
                        {participant.timeLeft ? dayjs(participant.timeLeft).format('HH:mm:ss') : '—'}
                      </TableCell>
                      <TableCell>{timeInRoom} mins</TableCell>
                      <TableCell>{participant.city || '—'}</TableCell>
                      <TableCell>{participant.country || '—'}</TableCell>
                      <TableCell>
                        {participant.owner && <Chip label="Owner" size="small" color="primary" sx={{ mr: 0.5 }} />}
                        {participant.isSystemUser && (
                          <Chip
                            label={
                              participant.odotId?.startsWith('recorder_') ? 'Recorder' :
                              participant.odotId?.startsWith('streamer_') ? 'Streamer' :
                              participant.odotId?.startsWith('rectrans_') ? 'Rec+Trans' :
                              participant.odotId?.startsWith('transcriber_') ? 'Transcriber' :
                              'System'
                            }
                            size="small"
                            color="warning"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography sx={{
            color: "text.secondary"
          }}>No participants found</Typography>
        )}
      </Paper>
      {/* Recordings */}
      {recordings.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recordings ({recordings.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {recordings.map((recording, index) => (
              <Chip
                key={recording.id}
                icon={<FiberManualRecordIcon />}
                label={`Recording ${index + 1} - ${recording.status}`}
                onClick={() => navigate(`/recordings/${recording.recordingId}`)}
                color={['completed', 'files uploaded'].includes(recording.status) ? 'success' : recording.status === 'recording' ? 'error' : 'default'}
                variant="outlined"
                clickable
              />
            ))}
          </Box>
        </Paper>
      )}
      {/* Streamings */}
      {streamings.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Streamings ({streamings.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {streamings.map((streaming, index) => (
              <Chip
                key={streaming.id}
                icon={<LiveTvIcon />}
                label={`Streaming ${index + 1} - ${streaming.status}`}
                onClick={() => navigate(`/streamings/${streaming.streamingId}`)}
                color={streaming.status === 'streaming' ? 'primary' : 'default'}
                variant="outlined"
                clickable
              />
            ))}
          </Box>
        </Paper>
      )}
      {/* Transcriptions */}
      {transcriptions.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Transcriptions ({transcriptions.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {transcriptions.map((transcription, index) => (
              <Chip
                key={transcription.id}
                icon={<SubtitlesIcon />}
                label={`Transcription ${index + 1} - ${transcription.status}`}
                onClick={() => navigate(`/transcriptions/${transcription.transcriptionId}`)}
                color={transcription.status === 'completed' ? 'success' : 'default'}
                variant="outlined"
                clickable
              />
            ))}
          </Box>
        </Paper>
      )}
      {/* Stats Dialog */}
      <RoomStatsDialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        roomName={room.name}
      />
    </Box>
  );
}

export default RoomDetails;
